import React, { useEffect, useState } from 'react';
import { companyAdminAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Users, Plus, Loader2, Upload, UserCheck, Trash2, Pencil, Building2, CreditCard, Search, ChevronDown, ChevronUp, CheckSquare, Square, MinusSquare } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_FORM = { employee_id: '', email: '', first_name: '', last_name: '', phone: '', department: '', job_title: '', employment_type: 'full_time', gross_salary: '' };

export default function EmployeeManagement() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [activatingVA, setActivatingVA] = useState(null);
  const [companyBanking, setCompanyBanking] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  const load = async () => { try { setEmployees(await companyAdminAPI.getEmployees()); } catch {} finally { setLoading(false); } };
  useEffect(() => {
    load();
    companyAdminAPI.getCompanyBanking().then(setCompanyBanking).catch(() => {});
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try { await companyAdminAPI.createEmployee({ ...form, gross_salary: parseFloat(form.gross_salary) }); toast.success('Employee created'); setAddOpen(false); setForm(EMPTY_FORM); load(); setCurrentPage(1); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to create employee'); }
    finally { setSubmitting(false); }
  };

  const handleCSV = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await companyAdminAPI.uploadCSV(fd);
      const parts = [`${res.created} created`];
      if (res.skipped > 0) parts.push(`${res.skipped} skipped (already exist)`);
      if (res.errors?.length) parts.push(`${res.errors.length} errors`);
      toast.success(`Upload complete: ${parts.join(', ')}`, { duration: 8000 });
      if (res.skipped_details?.length > 0) {
        res.skipped_details.forEach(s => toast.warning(`Skipped: ${s.email} — ${s.reason}`, { duration: 6000 }));
      }
      if (res.errors?.length > 0) {
        res.errors.forEach(e => toast.error(`Row ${e.row}: ${e.error}`, { duration: 6000 }));
      }
      load(); setCsvOpen(false); setCurrentPage(1);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed. Please check the CSV format and try again.'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleActivate = async (id) => {
    try { await companyAdminAPI.activateApp(id); toast.success('App access activated'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleEdit = (emp) => {
    setEditId(emp.id);
    setEditForm({
      first_name: emp.first_name || '', last_name: emp.last_name || '', phone: emp.phone || '',
      department: emp.department || '', job_title: emp.job_title || '', gross_salary: emp.gross_salary || '',
      bvn: '', nin: '', bank_name: emp.bank_name || '', account_number: emp.account_number || '', account_name: emp.account_name || '',
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const data = { ...editForm };
      if (data.gross_salary) data.gross_salary = parseFloat(data.gross_salary);
      if (!data.bvn) delete data.bvn;
      if (!data.nin) delete data.nin;
      await companyAdminAPI.updateEmployee(editId, data);
      toast.success('Employee updated'); setEditOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (emp) => {
    try { await companyAdminAPI.deleteEmployee(emp.id); toast.success(`${emp.first_name} ${emp.last_name} removed`); setDeleteConfirm(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); setDeleteConfirm(null); }
  };

  const handleActivateVA = async (emp) => {
    setActivatingVA(emp.id);
    try {
      const res = await companyAdminAPI.createEmployeeBankAccount(emp.id);
      if (res.status === 'exists') toast.info(`Already has account: ${res.account_number}`);
      else toast.success(`Virtual account created: ${res.account_number}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create virtual account'); }
    finally { setActivatingVA(null); }
  };

  // Selection helpers
  const filtered = employees.filter(e =>
    !search || `${e.first_name} ${e.last_name} ${e.email} ${e.department || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(e => e.id)));
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const res = await companyAdminAPI.bulkDeleteEmployees(Array.from(selectedIds));
      const msgs = [];
      if (res.deleted > 0) msgs.push(`${res.deleted} removed`);
      if (res.skipped > 0) msgs.push(`${res.skipped} skipped`);
      toast.success(msgs.join(', '));
      if (res.details?.skipped?.length > 0) {
        res.details.skipped.forEach(s => toast.warning(`${s.name || s.id}: ${s.reason}`));
      }
      setSelectedIds(new Set());
      setBulkDeleteConfirm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk delete failed'); }
    finally { setBulkDeleting(false); }
  };

  const allSelected = filtered.length > 0 && selectedIds.size === filtered.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < filtered.length;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  return (
    <div className="space-y-5" data-testid="employee-management">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div><h1 className="text-xl font-bold text-gray-900">Employees</h1><p className="text-sm text-gray-500">{employees.length} total</p></div>
        <div className="flex gap-2">
          <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
            <DialogTrigger asChild><Button variant="outline" className="border-gray-200 text-gray-600" data-testid="csv-upload-trigger"><Upload className="w-4 h-4 mr-2" /> CSV Upload</Button></DialogTrigger>
            <DialogContent className="border-gray-200"><DialogHeader><DialogTitle>Bulk Upload Employees</DialogTitle></DialogHeader>
              <p className="text-sm text-gray-500 mb-2">CSV must include: <strong>email, first_name, last_name</strong></p>
              <p className="text-xs text-gray-400 mb-3">Optional: employee_id, phone, department, job_title, gross_salary, nin, bank_name, account_number</p>
              <Input type="file" accept=".csv" onChange={handleCSV} disabled={uploading} className="h-9" data-testid="csv-upload-input" />
              {uploading && <div className="flex items-center gap-2 text-sm text-pink-600 mt-2"><Loader2 className="w-4 h-4 animate-spin" /> Processing CSV... please wait</div>}
            </DialogContent>
          </Dialog>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild><Button className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="add-employee-btn"><Plus className="w-4 h-4 mr-2" /> Add Employee</Button></DialogTrigger>
            <DialogContent className="border-gray-200 max-h-[80vh] overflow-y-auto"><DialogHeader><DialogTitle>Add Employee</DialogTitle></DialogHeader>
              <form onSubmit={handleCreate} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-sm">First Name</Label><Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} required className="h-9" /></div>
                  <div><Label className="text-sm">Last Name</Label><Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} required className="h-9" /></div>
                </div>
                <div><Label className="text-sm">Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required className="h-9" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-sm">Employee ID</Label><Input value={form.employee_id} onChange={e => setForm({...form, employee_id: e.target.value})} className="h-9" /></div>
                  <div><Label className="text-sm">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="h-9" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-sm">Department</Label><Input value={form.department} onChange={e => setForm({...form, department: e.target.value})} className="h-9" /></div>
                  <div><Label className="text-sm">Job Title</Label><Input value={form.job_title} onChange={e => setForm({...form, job_title: e.target.value})} className="h-9" /></div>
                </div>
                <div><Label className="text-sm">Gross Salary (NGN)</Label><Input type="number" value={form.gross_salary} onChange={e => setForm({...form, gross_salary: e.target.value})} required className="h-9" /></div>
                <Button type="submit" disabled={submitting} className="w-full bg-pink-500 hover:bg-pink-600 text-white">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Employee'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Company Banking Details */}
      {companyBanking?.virtual_account_number && (
        <Card className="border-blue-100 bg-blue-50/30" data-testid="company-banking-card">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
              <Building2 className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] text-gray-400 uppercase font-medium">Company Receiving Account</p>
              <p className="text-sm font-bold text-gray-900">{companyBanking.virtual_account_number} <span className="font-normal text-gray-500">| {companyBanking.virtual_account_bank}</span></p>
              <p className="text-xs text-gray-500">{companyBanking.company_name}</p>
            </div>
            <div className="text-right hidden sm:block">
              <p className="text-[10px] text-gray-400">Balance</p>
              <p className="text-sm font-bold" style={{ fontFamily: 'JetBrains Mono' }}>NGN {Number(companyBanking.wallet_balance || 0).toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search + Bulk Actions */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={search} onChange={e => { setSearch(e.target.value); setSelectedIds(new Set()); setCurrentPage(1); }} placeholder="Search employees..." className="pl-9 h-9" data-testid="employee-search" />
        </div>
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2" data-testid="bulk-actions-bar">
            <span className="text-sm text-gray-600 font-medium">{selectedIds.size} selected</span>
            <Button size="sm" variant="outline" className="h-8 text-xs border-red-200 text-red-600 hover:bg-red-50"
              onClick={() => setBulkDeleteConfirm(true)} data-testid="bulk-delete-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Selected
            </Button>
            <Button size="sm" variant="ghost" className="h-8 text-xs text-gray-500"
              onClick={() => setSelectedIds(new Set())} data-testid="clear-selection-btn">
              Clear
            </Button>
          </div>
        )}
      </div>

      {/* Select All */}
      {filtered.length > 0 && (
        <div className="flex items-center gap-2 px-1">
          <button onClick={toggleSelectAll} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors" data-testid="select-all-btn">
            {allSelected ? <CheckSquare className="w-4 h-4 text-pink-500" /> : someSelected ? <MinusSquare className="w-4 h-4 text-pink-400" /> : <Square className="w-4 h-4 text-gray-400" />}
            <span>{allSelected ? 'Deselect all' : 'Select all'} ({filtered.length})</span>
          </button>
        </div>
      )}

      {/* Employee List */}
      <div className="space-y-2">
        {paged.map(e => {
          const isExpanded = expandedId === e.id;
          const isSelected = selectedIds.has(e.id);
          return (
            <Card key={e.id} className={`border-gray-200 shadow-sm transition-colors ${isSelected ? 'ring-1 ring-pink-300 bg-pink-50/30' : ''}`} data-testid={`emp-card-${e.id}`}>
              <CardContent className="p-0">
                {/* Main row */}
                <div className="p-3 flex items-center gap-3">
                  {/* Checkbox */}
                  <button onClick={(ev) => { ev.stopPropagation(); toggleSelect(e.id); }} className="shrink-0" data-testid={`emp-checkbox-${e.id}`}>
                    {isSelected ? <CheckSquare className="w-4.5 h-4.5 text-pink-500" /> : <Square className="w-4.5 h-4.5 text-gray-300 hover:text-gray-400" />}
                  </button>
                  {/* Content (clickable for expand) */}
                  <div className="flex items-center justify-between flex-1 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : e.id)}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-pink-50 text-pink-600 flex items-center justify-center text-xs font-bold">
                        {e.first_name?.[0]}{e.last_name?.[0]}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{e.first_name} {e.last_name}</p>
                        <p className="text-xs text-gray-500">{e.email} {e.department ? `| ${e.department}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 hidden sm:block">NGN {Number(e.gross_salary || 0).toLocaleString()}</span>
                      {e.has_app_access ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium">Active</span>
                      ) : (
                        <Button size="sm" variant="outline" onClick={(ev) => { ev.stopPropagation(); handleActivate(e.id); }} className="text-xs border-pink-200 text-pink-600 hover:bg-pink-50 h-7">
                          <UserCheck className="w-3 h-3 mr-1" /> Activate
                        </Button>
                      )}
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="border-t px-3 pb-3 pt-2 space-y-3 bg-gray-50/50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium mb-1">Employee Details</p>
                        <div className="text-xs space-y-1 text-gray-600">
                          <p><span className="text-gray-400 w-24 inline-block">Phone:</span> {e.phone || '\u2014'}</p>
                          <p><span className="text-gray-400 w-24 inline-block">Job Title:</span> {e.job_title || '\u2014'}</p>
                          <p><span className="text-gray-400 w-24 inline-block">Dept:</span> {e.department || '\u2014'}</p>
                          <p><span className="text-gray-400 w-24 inline-block">BVN:</span> {e.has_bvn ? <span className="text-emerald-600 font-medium">Added</span> : <span className="text-red-500 font-medium">Not Added</span>}</p>
                          <p><span className="text-gray-400 w-24 inline-block">NIN:</span> {e.has_nin ? <span className="text-emerald-600 font-medium">Added</span> : <span className="text-red-500 font-medium">Not Added</span>}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium mb-1">Banking Details</p>
                        <div className="text-xs space-y-1 text-gray-600">
                          {e.bank_name ? (
                            <>
                              <p><span className="text-gray-400 w-24 inline-block">Bank:</span> {e.bank_name}</p>
                              <p><span className="text-gray-400 w-24 inline-block">Account:</span> <span className="font-mono">{e.account_number || '\u2014'}</span></p>
                              <p><span className="text-gray-400 w-24 inline-block">Acct Name:</span> {e.account_name || '\u2014'}</p>
                            </>
                          ) : (
                            <p className="text-gray-400 italic">No banking details</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => handleEdit(e)} data-testid={`edit-emp-${e.id}`}>
                        <Pencil className="w-3 h-3 mr-1" /> Edit Details
                      </Button>
                      {!e.bank_name && (
                        <Button size="sm" variant="outline" className="h-8 text-xs border-blue-200 text-blue-600 hover:bg-blue-50"
                          onClick={() => handleActivateVA(e)} disabled={activatingVA === e.id} data-testid={`activate-va-${e.id}`}>
                          {activatingVA === e.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CreditCard className="w-3 h-3 mr-1" />}
                          Activate Virtual Account
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="h-8 text-xs border-red-200 text-red-500 hover:bg-red-50 ml-auto"
                        onClick={() => setDeleteConfirm(e)} data-testid={`delete-emp-${e.id}`}>
                        <Trash2 className="w-3 h-3 mr-1" /> Remove
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {paged.length === 0 && <p className="text-center text-sm text-gray-400 py-8">{search ? 'No employees match your search' : 'No employees yet'}</p>}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2" data-testid="pagination-controls">
          <p className="text-xs text-gray-500">
            Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8 px-3 text-xs"
              disabled={currentPage === 1} onClick={() => setCurrentPage(1)} data-testid="page-first">
              First
            </Button>
            <Button variant="outline" size="sm" className="h-8 w-8 text-xs"
              disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} data-testid="page-prev">
              &lt;
            </Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
              .reduce((acc, p, i, arr) => {
                if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === '...' ? <span key={`dots-${i}`} className="px-1 text-xs text-gray-400">...</span> :
                <Button key={p} variant={p === currentPage ? "default" : "outline"} size="sm"
                  className={`h-8 w-8 text-xs ${p === currentPage ? 'bg-pink-500 hover:bg-pink-600 text-white' : ''}`}
                  onClick={() => setCurrentPage(p)} data-testid={`page-${p}`}>
                  {p}
                </Button>
              )}
            <Button variant="outline" size="sm" className="h-8 w-8 text-xs"
              disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} data-testid="page-next">
              &gt;
            </Button>
            <Button variant="outline" size="sm" className="h-8 px-3 text-xs"
              disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)} data-testid="page-last">
              Last
            </Button>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="border-gray-200 max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>Edit Employee</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveEdit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">First Name</Label><Input value={editForm.first_name || ''} onChange={e => setEditForm({...editForm, first_name: e.target.value})} className="h-9" /></div>
              <div><Label className="text-sm">Last Name</Label><Input value={editForm.last_name || ''} onChange={e => setEditForm({...editForm, last_name: e.target.value})} className="h-9" /></div>
            </div>
            <div><Label className="text-sm">Phone</Label><Input value={editForm.phone || ''} onChange={e => setEditForm({...editForm, phone: e.target.value})} className="h-9" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Department</Label><Input value={editForm.department || ''} onChange={e => setEditForm({...editForm, department: e.target.value})} className="h-9" /></div>
              <div><Label className="text-sm">Job Title</Label><Input value={editForm.job_title || ''} onChange={e => setEditForm({...editForm, job_title: e.target.value})} className="h-9" /></div>
            </div>
            <div><Label className="text-sm">Gross Salary (NGN)</Label><Input type="number" value={editForm.gross_salary || ''} onChange={e => setEditForm({...editForm, gross_salary: e.target.value})} className="h-9" /></div>
            <hr className="my-2" />
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Identity & Banking</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">BVN <span className="text-gray-400 font-normal">(leave blank to keep)</span></Label><Input value={editForm.bvn || ''} onChange={e => setEditForm({...editForm, bvn: e.target.value})} placeholder="11-digit BVN" maxLength={11} className="h-9" /></div>
              <div><Label className="text-sm">NIN <span className="text-gray-400 font-normal">(leave blank to keep)</span></Label><Input value={editForm.nin || ''} onChange={e => setEditForm({...editForm, nin: e.target.value})} placeholder="11-digit NIN" maxLength={11} className="h-9" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Bank Name</Label><Input value={editForm.bank_name || ''} onChange={e => setEditForm({...editForm, bank_name: e.target.value})} className="h-9" /></div>
              <div><Label className="text-sm">Account Number</Label><Input value={editForm.account_number || ''} onChange={e => setEditForm({...editForm, account_number: e.target.value})} className="h-9" /></div>
            </div>
            <div><Label className="text-sm">Account Name</Label><Input value={editForm.account_name || ''} onChange={e => setEditForm({...editForm, account_name: e.target.value})} className="h-9" /></div>
            <Button type="submit" disabled={submitting} className="w-full bg-pink-500 hover:bg-pink-600 text-white h-10">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="border-gray-200 max-w-sm">
          <DialogHeader><DialogTitle className="text-red-600">Remove Employee</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">Are you sure you want to remove <strong>{deleteConfirm?.first_name} {deleteConfirm?.last_name}</strong>? This action cannot be undone.</p>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" className="flex-1" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button className="flex-1 bg-red-500 hover:bg-red-600 text-white" onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-btn">Remove</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation */}
      <Dialog open={bulkDeleteConfirm} onOpenChange={setBulkDeleteConfirm}>
        <DialogContent className="border-gray-200 max-w-sm">
          <DialogHeader><DialogTitle className="text-red-600">Delete {selectedIds.size} Employees</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">
            Are you sure you want to remove <strong>{selectedIds.size} employees</strong>? Employees with a wallet balance will be skipped. This action cannot be undone.
          </p>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" className="flex-1" onClick={() => setBulkDeleteConfirm(false)} data-testid="cancel-bulk-delete-btn">Cancel</Button>
            <Button className="flex-1 bg-red-500 hover:bg-red-600 text-white" onClick={handleBulkDelete} disabled={bulkDeleting} data-testid="confirm-bulk-delete-btn">
              {bulkDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Delete {selectedIds.size}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
